
import { replaceAll } from '@/assets/script/utils/index.js'

// Variabile per tracciare lo stato di caricamento dello script di Instagram a livello di applicazione (client-side)
let instagramScriptLoaded = false
let instagramScriptLoading = false
const instagramScriptId = 'instagram-embed-script'

export default {
  name: 'ArticleContent', // Nome del componente rivisto per chiarezza
  props: {
    post: {
      type: Object,
      required: true,
      default: () => ({ content: '', slug: '' }), // Buona pratica: fornire un default per le props oggetto
    },
  },
  computed: {
    parsedContent() {
      let content = this.post?.content || ''

      // 1) Rimuovi lo script di Instagram dal contenuto HTML se presente,
      //    poiché lo gestiamo noi per assicurarne il caricamento singolo.
      content = content.replace(
        /<script[^>]*src=["']\/\/www\.instagram\.com\/embed\.js["'][^>]*><\/script>/gi,
        ''
      )

      // 2) Sposta gli attributi 'style' in 'data-wp-style',
      //    preservando gli stili per 'adsbygoogle' e 'instagram-media'.
      content = content.replace(
        /(<[^>]*\bstyle\s*=\s*["'][^"']*["'][^>]*>)/g,
        (match) => {
          if (
            /class\s*=\s*["'][^"']*\b(adsbygoogle|instagram-media)\b[^"']*["']/.test(
              match
            )
          ) {
            return match
          }
          return match.replace(
            /(\sstyle\s*=\s*["'])([^"']*)(["'])/g,
            ' data-wp-style="$2"$3'
          )
        }
      )

      // 3) Sostituisci gli URL base (assicurati che le variabili d'ambiente siano definite)
      const wordpressUrl = process.env.NUXT_ENV_WORDPRESS_BASE_URL
      const appBaseUrl = process.env.NUXT_ENV_BASE_URL
      if (wordpressUrl && appBaseUrl && typeof replaceAll === 'function') {
        content = replaceAll(content, wordpressUrl, appBaseUrl)
      }

      // 4) Regola gli anchor interni (assicurati che this.post.slug sia definito)
      if (this.post.slug && typeof replaceAll === 'function') {
        // Considera il routing di Nuxt. Se lo slug è 'mio-articolo', l'URL sarà '/mio-articolo/#sezione'
        content = replaceAll(content, 'href="#', `href="/${this.post.slug}/#`)
      }

      return content
    },
  },
  watch: {
    // Se il contenuto del post cambia, riesegui l'inizializzazione
    // dopo che il DOM è stato aggiornato.
    'post.content'() {
      this.$nextTick(() => {
        this.initializePlugins()
      })
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initializePlugins()
    })
  },
  methods: {
    initializePlugins() {
      if (!this.$refs.articleContentElement) return

      this.initializeFlickityGalleries()
      this.loadInstagramScriptAndProcessEmbeds()
    },

    initializeFlickityGalleries() {
      try {
        const Flickity = require('flickity')
        const galleries = this.$refs.articleContentElement.querySelectorAll(
          '.gallery, .wp-block-gallery'
        )
        if (galleries.length > 0) {
          Array.from(galleries).forEach((gallery) => {
            if (Flickity && !Flickity.data(gallery)) {
              // Evita reinizializzazioni
              // eslint-disable-next-line
              new Flickity(gallery, {
                groupCells: true,
                prevNextButtons: true,
                cellAlign: 'left',
                adaptiveHeight: true,
                arrowShape:
                  'M60 10.545 28.33 42.5H100v15H28.33L60 89.455 49.55 100 0 50 49.55 0z',
              })
            }
          })
        }
      } catch (error) {
        console.error("Errore durante l'inizializzazione di Flickity:", error)
      }
    },

    loadInstagramScriptAndProcessEmbeds() {
      if (typeof window === 'undefined') return // Esegui solo lato client

      if (instagramScriptLoaded) {
        // Se lo script è già stato caricato e inizializzato, processa gli embed.
        this.processInstagramEmbeds()
        return
      }

      if (instagramScriptLoading) {
        // Se lo script è in fase di caricamento, attendi l'evento 'load'
        // o processa se `window.instgrm` diventa disponibile.
        // Questo previene chiamate multiple a `process` mentre lo script carica.
        const intervalId = setInterval(() => {
          if (instagramScriptLoaded) {
            clearInterval(intervalId)
            this.processInstagramEmbeds()
          } else if (typeof window.instgrm?.Embeds?.process === 'function') {
            // A volte window.instgrm potrebbe essere pronto prima che 'load' scatti se lo script era cachato
            clearInterval(intervalId)
            instagramScriptLoaded = true // Segna come caricato
            this.processInstagramEmbeds()
          }
        }, 100)
        // Timeout di sicurezza per pulire l'intervallo
        setTimeout(() => clearInterval(intervalId), 5000)
        return
      }

      // Se lo script non è né caricato né in caricamento, avvia il caricamento.
      // Questo blocco verrà eseguito solo dalla prima istanza del componente
      // che necessita dello script.
      if (!document.getElementById(instagramScriptId)) {
        instagramScriptLoading = true
        const script = document.createElement('script')
        script.id = instagramScriptId
        script.src = 'https://www.instagram.com/embed.js' // Usa https:
        script.async = true
        script.defer = true // defer è preferibile per script non critici per il rendering iniziale

        script.onload = () => {
          instagramScriptLoaded = true
          instagramScriptLoading = false
          // console.log('Instagram embed.js caricato con successo.');
          this.processInstagramEmbeds() // Processa gli embed dopo il caricamento
        }
        script.onerror = () => {
          instagramScriptLoading = false
          console.error(
            'Errore durante il caricamento dello script di Instagram embed.js.'
          )
        }
        document.body.appendChild(script)
      } else {
        // Lo script esiste già nel DOM (magari aggiunto da altro o da nuxt.config.js)
        // Verifica se window.instgrm è pronto
        // eslint-disable-next-line
        if (typeof window.instgrm?.Embeds?.process === 'function') {
          instagramScriptLoaded = true
          this.processInstagramEmbeds()
        } else {
          // Attendi che sia pronto (lo script è nel DOM ma potrebbe non aver ancora eseguito il suo codice)
          const checkReadyInterval = setInterval(() => {
            if (typeof window.instgrm?.Embeds?.process === 'function') {
              clearInterval(checkReadyInterval)
              instagramScriptLoaded = true
              this.processInstagramEmbeds()
            }
          }, 100)
          setTimeout(() => clearInterval(checkReadyInterval), 5000) // Timeout di sicurezza
        }
      }
    },

    processInstagramEmbeds() {
      if (typeof window.instgrm?.Embeds?.process === 'function') {
        window.instgrm.Embeds.process()
      } else {
        // Questo fallback è meno probabile se la logica di caricamento è corretta
        console.warn(
          'API Instagram Embeds non disponibile al momento del processamento.'
        )
        // Potresti considerare un ultimo tentativo con un breve ritardo
        // setTimeout(() => {
        //   if (typeof window.instgrm?.Embeds?.process === 'function') {
        //     window.instgrm.Embeds.process();
        //   }
        // }, 500);
      }
    },
  },
}
